import { useEffect, useId, useRef, type MouseEvent, type ReactNode } from 'react';
import { cx } from './cx';
import { IconButton } from './Button';
import { IconClose } from './icons';
import { useKit } from './strings';

/**
 * Modal dialog on the native <dialog>: top layer, inert background, focus moved in and restored on close, Esc closes.
 * Controlled: pass open and onClose. Clicking the backdrop closes unless dismissible is false (use that for forms
 * with unsaved input). The first focusable element (or [autofocus]) receives focus.
 */
export function Dialog({ open, onClose, title, description, children, footer, size = 'md', dismissible = true, className, labelledBy }: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  dismissible?: boolean;
  className?: string;
  /** Use when the dialog renders its own heading instead of title. */
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const base = useId().replace(/:/g, '');
  const { strings } = useKit();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal?.();
    else if (!open && el.open) el.close();
  }, [open]);
  const onBackdrop = (e: MouseEvent<HTMLDialogElement>) => { if (dismissible && e.target === e.currentTarget) onClose(); };
  return (
    <dialog
      ref={ref}
      className={cx('qk-dialog', 'qk', className)}
      data-size={size}
      aria-labelledby={labelledBy ?? (title ? `${base}-t` : undefined)}
      aria-describedby={description ? `${base}-d` : undefined}
      onCancel={(e) => { e.preventDefault(); if (dismissible) onClose(); }}
      onClick={onBackdrop}
    >
      {open ? (
        <>
          {title ? (
            <header className="qk-dialog__header">
              <div className="qk-dialog__heading">
                <h2 className="qk-dialog__title" id={`${base}-t`}>{title}</h2>
                {description ? <p className="qk-dialog__desc" id={`${base}-d`}>{description}</p> : null}
              </div>
              <IconButton label={strings.close} size="sm" onClick={onClose}><IconClose /></IconButton>
            </header>
          ) : null}
          {children !== undefined ? <div className="qk-dialog__body">{children}</div> : null}
          {footer ? <footer className="qk-dialog__footer">{footer}</footer> : null}
        </>
      ) : null}
    </dialog>
  );
}
